import { useEffect, useState, type ReactNode } from 'react';
import { Spinner } from '@telegram-apps/telegram-ui';
import { useLaunchParams } from '@telegram-apps/sdk-react';
import { BottomNav } from '../components/design/BottomNav';
import { brand, fontFamily, gold, tg } from '../theme/tokens';
import { listGenerations, type Generation } from '../lib/api';

/**
 * Profile tab — iOS-grouped-list pattern (sections of rounded card rows
 * with colored icon-box + label + value + chevron). Native TG-app feel.
 *
 * Sections:
 *   - User strip — avatar + name + handle + premium badge (compact, no big card)
 *   - Activity — real stats from /api/generations (created/successful/refunded)
 *   - Preferences — placeholders for language & notifications (real wiring TBD)
 *   - Support — help / privacy / terms / contact (links TBD)
 *
 * Stars-balance row intentionally omitted: Telegram doesn't expose user
 * balance to bots, only individual charges we initiated.
 */
export function ProfilePage() {
  const lp = useLaunchParams(true);
  const user = lp.tgWebAppData?.user;
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    listGenerations({ limit: 100 })
      .then((r) => setStats(computeStats(r.items)))
      .catch(() => setStats({ total: 0, done: 0, failed: 0 }));
  }, []);

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Telegram user';
  const username = user?.username ? `@${user.username}` : '';
  const initials = initialsOf(user?.firstName, user?.lastName);

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', paddingBottom: 100, fontFamily }}>
      {/* Page title */}
      <div style={{ padding: '16px 20px 6px' }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: tg.text, letterSpacing: -0.3 }}>
          Profile
        </div>
      </div>

      {/* User strip — compact identity row */}
      <UserStrip
        photoUrl={user?.photoUrl}
        initials={initials}
        fullName={fullName}
        username={username}
        isPremium={!!user?.isPremium}
      />

      {/* Activity section */}
      <SectionLabel>Activity</SectionLabel>
      <Section>
        <Row
          icon={<SparkleIcon />}
          iconBg="linear-gradient(135deg, #ff6b9d, #c44569)"
          label="Kisses created"
          value={statValue(stats?.total)}
        />
        <Row
          icon={<HeartIcon />}
          iconBg="linear-gradient(135deg, #34c759, #2da94a)"
          label="Successful"
          value={statValue(stats?.done)}
        />
        <Row
          icon={<RefundIcon />}
          iconBg="linear-gradient(135deg, #8e8e93, #636366)"
          label="Refunded"
          value={statValue(stats?.failed)}
          // last row — no chevron (non-clickable info)
          noChevron
        />
      </Section>

      {/* Preferences */}
      <SectionLabel>Preferences</SectionLabel>
      <Section>
        <Row
          icon={<GlobeIcon />}
          iconBg="linear-gradient(135deg, #5ac8fa, #007aff)"
          label="Language"
          value="English"
        />
        <Row
          icon={<BellIcon />}
          iconBg="linear-gradient(135deg, #ff9500, #ff5e3a)"
          label="Notifications"
          value="On"
        />
      </Section>

      {/* Support */}
      <SectionLabel>Support</SectionLabel>
      <Section>
        <Row
          icon={<HelpIcon />}
          iconBg="linear-gradient(135deg, #34c759, #2da94a)"
          label="Help center"
        />
        <Row
          icon={<ShieldIcon />}
          iconBg="linear-gradient(135deg, #8e8e93, #636366)"
          label="Privacy Policy"
        />
        <Row
          icon={<DocIcon />}
          iconBg="linear-gradient(135deg, #af52de, #8e44ad)"
          label="Terms of Service"
        />
        <Row
          icon={<MessageIcon />}
          iconBg="linear-gradient(135deg, #6169ee, #4f56cc)"
          label="Contact us"
        />
      </Section>

      {/* Footer */}
      <div
        style={{
          textAlign: 'center',
          fontSize: 12,
          color: tg.textHint,
          padding: '16px 16px 12px',
        }}
      >
        DreamKisses · v0.1.0
      </div>

      <BottomNav />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Section building blocks
// ─────────────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: '14px 20px 6px',
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--tgui--section_header_text_color, ' + tg.textHint + ')',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      {children}
    </div>
  );
}

function Section({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        margin: '0 16px 6px',
        background: tg.bg2,
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

function Row({
  icon,
  iconBg,
  label,
  value,
  noChevron,
}: {
  icon: ReactNode;
  iconBg: string;
  label: string;
  value?: ReactNode;
  noChevron?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '11px 14px',
        borderBottom: `0.5px solid ${tg.divider}`,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: '#fff',
        }}
      >
        {icon}
      </div>
      <span style={{ flex: 1, fontSize: 15, color: tg.text, fontWeight: 400 }}>
        {label}
      </span>
      {value !== undefined && (
        <span style={{ fontSize: 15, color: tg.textHint, fontWeight: 400 }}>
          {value}
        </span>
      )}
      {!noChevron && <Chevron />}
    </div>
  );
}

function Chevron() {
  return (
    <svg
      width={10}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--tgui--hint_color)"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginLeft: 2, flexShrink: 0 }}
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────
// User strip
// ─────────────────────────────────────────────────────────────────────

function UserStrip({
  photoUrl,
  initials,
  fullName,
  username,
  isPremium,
}: {
  photoUrl?: string;
  initials: string;
  fullName: string;
  username: string;
  isPremium: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        margin: '6px 16px 14px',
        padding: '12px 14px',
        background: tg.bg2,
        borderRadius: 14,
      }}
    >
      <Avatar photoUrl={photoUrl} initials={initials} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: tg.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fullName}
        </div>
        {username && (
          <div style={{ fontSize: 12, color: tg.textHint, marginTop: 1 }}>
            {username}
          </div>
        )}
      </div>
      {isPremium && (
        <div
          style={{
            padding: '3px 9px',
            borderRadius: 999,
            background: gold.gradient,
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.3,
            flexShrink: 0,
          }}
        >
          ⭐ Premium
        </div>
      )}
    </div>
  );
}

function Avatar({ photoUrl, initials }: { photoUrl?: string; initials: string }) {
  if (photoUrl) {
    return (
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 999,
          overflow: 'hidden',
          background: tg.bg3,
          flexShrink: 0,
        }}
      >
        <img
          src={photoUrl}
          alt=""
          style={{ width: '100%', height: '100%', display: 'block' }}
        />
      </div>
    );
  }
  // Fallback initials with brand gradient
  return (
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 999,
        background: brand.gradient,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 16,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stats helpers
// ─────────────────────────────────────────────────────────────────────

interface Stats {
  total: number;
  done: number;
  failed: number;
}

function computeStats(items: Generation[]): Stats {
  let done = 0;
  let failed = 0;
  for (const g of items) {
    if (g.status === 'done') done++;
    else if (g.status === 'failed') failed++;
  }
  return { total: items.length, done, failed };
}

function statValue(n: number | undefined): ReactNode {
  if (n === undefined) return <Spinner size="s" />;
  return n.toString();
}

function initialsOf(first?: string, last?: string): string {
  return ((first?.[0] ?? '') + (last?.[0] ?? '')).toUpperCase() || 'U';
}

// ─────────────────────────────────────────────────────────────────────
// Inline icons (16x16 each, white via parent color)
// ─────────────────────────────────────────────────────────────────────

const iconProps = {
  width: 15,
  height: 15,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function SparkleIcon() {
  return (
    <svg {...iconProps} fill="currentColor" stroke="none">
      <path d="M12 2l1.5 4.5h4.5l-3.6 2.7 1.3 4.4-3.7-2.7L8.3 13.6l1.3-4.4L6 6.5h4.5L12 2z" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg {...iconProps} fill="currentColor" stroke="none">
      <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z" />
    </svg>
  );
}

function RefundIcon() {
  return (
    <svg {...iconProps}>
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg {...iconProps}>
      <path d="M18 16v-5a6 6 0 1 0-12 0v5l-2 2v1h16v-1l-2-2z" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9a3 3 0 0 1 6 0c0 2-3 2-3 4" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg {...iconProps}>
      <path d="M12 2l9 4v6c0 5-4 9-9 10-5-1-9-5-9-10V6l9-4z" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg {...iconProps}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg {...iconProps}>
      <path d="M21 11.5a8.4 8.4 0 0 1-1 4 8.5 8.5 0 0 1-7.6 4.5 8.4 8.4 0 0 1-4-1L3 21l2-5.4a8.4 8.4 0 0 1-1-4 8.5 8.5 0 0 1 4.5-7.5 8.4 8.4 0 0 1 4-1H13a8.5 8.5 0 0 1 8 8z" />
    </svg>
  );
}
